<template>
	<v-dialog :attach="overlayTarget" :activator="activator" v-model="activeDialog" eager>
		<v-card>
			<v-card-title class="headline">
				<md-icon>filter_alt</md-icon>
				{{ t('Filters') }}
			</v-card-title>
			<v-card-text>
				<SelectControl ref="deliveryLocationAccountSelect" :label="t('Accounts')" :items="deliveryLocationAccounts" :multiple="true" />
				<SelectControl ref="deliveryLocationChannelSelect" :label="t('Channels')" :items="deliveryLocationChannels" :multiple="true" />
				<SelectControl ref="deliveryLocationTypeSelect" :label="t('DeliveryLocationType')" :items="deliveryLocationTypes" :multiple="true" />
				<SelectControl ref="deliveryLocationGroupSelect" :label="t('Groups')" :items="deliveryLocationGroups" :multiple="true" />
				<SelectControl ref="deliveryLocationUnreachedKpiSelect" :label="t('KpiNotReached')" :items="deliveryLocationKpis" :title-key="'reference'" :title-key-translate="true" :multiple="true" />
				<SelectControl ref="deliveryLocationCanalSelect" :label="t('DistributionChannels')" :items="deliveryLocationCanals" :title-key="'label'" :multiple="true" />
				<SelectControl ref="deliveryLocationPosTypeSelect" :label="t('POSType')" :items="deliveryLocationPosTypes" :title-key="'label'" :multiple="true" />
				<SelectControl ref="deliveryLocationPostalCodeSelect" :label="t('Postcode')" :items="deliveryLocationPostalCodes" :multiple="true" />
				<NumberControl ref="deliveryLocationFreshnessNumber" :label="t('Freshness')" v-if="hasSFA()" />
			</v-card-text>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text @click="resetFilters" v-if="hasDirtyFilter">{{ t('Reset') }}</v-btn>
				<v-btn text @click="activeDialog = false" data-close>{{ t('Close') }}</v-btn>
				<v-btn variant="tonal" @click="applyFilters">{{ t('Save') }}</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script setup>
// #region Imports

import { ref, onMounted, inject, computed } from 'vue';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { useTranslator } from '@/utils/useTranslator';
import { hasSFA } from '@/utils/processUtils.js';
import { getLastVisitInfo, hydrateLocationsKpis } from '@/utils/appointmentUtils.js';

import SelectControl from '@/components/controls/SelectControl.vue';
import NumberControl from '@/components/controls/NumberControl.vue';

// #endregion

// #region Refs & Vars

const emit = defineEmits(['filters-changed']);
const props = defineProps({
	activator: { type: [Object, String], default: null },
	locations: { type: Array, default: () => [] },
});

const t = useTranslator();
const activeDialog = ref(false);
const overlayTarget = inject('overlayTarget', null);
const originalLocations = ref([]);
const ecosVM = iframeDataBridge.getEcosVM();
const sfaHelper = iframeDataBridge.getSfaHelper();
const dateHelper = iframeDataBridge.getDateHelper();
const hasSFAProcess = hasSFA();

const hasDirtyFilter = computed(() => {
	return getFiltersDirtyState();
});

const deliveryLocationTypes = ecosVM.deliveryLocationTypes || [];
const deliveryLocationAccounts = ecosVM.accounts() || [];
const deliveryLocationChannels = ecosVM.channels || [];
const deliveryLocationGroups = ecosVM.DLGroups || [];
const deliveryLocationCanals = ecosVM.distributionChannels || [];
const deliveryLocationPosTypes = ecosVM.posTypes || [];
const deliveryLocationPostalCodes = props.locations
	.map((dl) => getPostalCodePrefix(dl.postcode))
	.filter((prefix, index, allPrefixes) => prefix && allPrefixes.indexOf(prefix) === index)
	.sort((a, b) => a.localeCompare(b, 'fr', { numeric: true, sensitivity: 'base' }))
	.map((prefix) => {
		return { id: prefix, name: prefix };
	});
let kpiExcluded = ['OrderAssortWeight', 'OrderAssortValue', 'OrderAssortWeightDelivered', 'OrderAssortValueDelivered'];
const deliveryLocationKpis = hasSFAProcess ? ecosVM.kpis.filter((k) => k && k.reference && !kpiExcluded.includes(k.reference)) : [];

const deliveryLocationTypeSelect = ref(null);
const deliveryLocationAccountSelect = ref(null);
const deliveryLocationChannelSelect = ref(null);
const deliveryLocationGroupSelect = ref(null);
const deliveryLocationCanalSelect = ref(null);
const deliveryLocationPosTypeSelect = ref(null);
const deliveryLocationPostalCodeSelect = ref(null);
const deliveryLocationFreshnessNumber = ref(null);
const deliveryLocationUnreachedKpiSelect = ref(null);

// #endregion

// #endregion

// #region Lifecycle Hooks

onMounted(() => {
	originalLocations.value = Array.from(props.locations);
});

// #endregion

// #region Watchers

// #endregion

// #region Methods

async function applyFilters(locations = null) {
	locations = locations && Array.isArray(locations) ? locations : originalLocations.value;
	originalLocations.value = Array.from(locations);

	const selectedTypeIds = deliveryLocationTypeSelect.value.getValues() || [];
	const selectedAccountIds = deliveryLocationAccountSelect.value.getValues() || [];
	const selectedChannelIds = deliveryLocationChannelSelect.value.getValues() || [];
	const selectedGroupIds = deliveryLocationGroupSelect.value.getValues() || [];
	const selectedCanalIds = deliveryLocationCanalSelect.value.getValues() || [];
	const selectedPosTypeIds = deliveryLocationPosTypeSelect.value.getValues() || [];
	const selectedPostalCodeIds = deliveryLocationPostalCodeSelect.value.getValues() || [];
	const selectedFreshness = hasSFAProcess ? Number(deliveryLocationFreshnessNumber.value.getValue() || 0) : 0;
	const selectedKpiIds = hasSFAProcess ? deliveryLocationUnreachedKpiSelect.value.getValues() || [] : [];

	// Get last appointment for freshness filtering
	const hasAppointmentsLoaded = locations.map((dl) => dl.lastAppointment).filter((a) => a && a.startDate).length > 0;
	const lastAppointments =
		hasSFAProcess && selectedFreshness > 0 && !hasAppointmentsLoaded
			? await getLastVisitInfo(
					null,
					locations.map((dl) => dl.id)
				)
			: locations.map((dl) => dl.lastAppointment).filter((a) => a && a.startDate);

	// Get KPIs data for KPI filtering
	if (hasSFAProcess && selectedKpiIds.length > 0) {
		await hydrateLocationsKpis(locations, selectedKpiIds);
	}

	const today = new Date();
	const filteredLocations = locations.filter((location) => {
		const lastAppointment = lastAppointments ? lastAppointments.find((la) => la.deliveryLocationId === location.id) : null;
		if (lastAppointment && lastAppointment.startDate) {
			location.lastAppointment = lastAppointment;
		}
		const hasFutureAppointment = location.nextAppointmentDate && new Date(location.nextAppointmentDate) > today;
		const isTooOld = !location.lastAppointment || dateHelper.getDiffDays(location.lastAppointment.startDate, today) >= selectedFreshness;

		const matchesType = selectedTypeIds.length === 0 || selectedTypeIds.includes(location.deliveryLocationTypeId);
		const matchesAccount = selectedAccountIds.length === 0 || selectedAccountIds.includes(location.accountId);
		const matchesChannel = selectedChannelIds.length === 0 || selectedChannelIds.includes(location.channelId);
		const matchesGroup = selectedGroupIds.length === 0 || selectedGroupIds.includes(location.groupId);
		const matchesCanal = selectedCanalIds.length === 0 || selectedCanalIds.includes(location.distributionChannel);
		const matchesPosType = selectedPosTypeIds.length === 0 || selectedPosTypeIds.includes(location.posTypeId);
		const matchesPostalCode = selectedPostalCodeIds.length === 0 || selectedPostalCodeIds.includes(getPostalCodePrefix(location.postcode));
		const matchesFreshness = selectedFreshness <= 0 || (isTooOld && !hasFutureAppointment);
		const matchesKpi = selectedKpiIds.length === 0 || sfaHelper.kpi.evaluateKpis(location, Array.from(selectedKpiIds));

		return matchesType && matchesAccount && matchesChannel && matchesGroup && matchesCanal && matchesPosType && matchesPostalCode && matchesFreshness && matchesKpi;
	});

	emit('filters-changed', { filteredLocations, dirty: getFiltersDirtyState() });
	activeDialog.value = false;
}

function originalLocationsUpdated(newLocations) {
	originalLocations.value = Array.from(newLocations);
}

function getPostalCodePrefix(postcode) {
	const normalizedPostcode = String(postcode ?? '').trim();
	return normalizedPostcode ? normalizedPostcode.substring(0, 2).toUpperCase() : null;
}

function getFiltersDirtyState() {
	try {
		return deliveryLocationTypeSelect.value.isDirty() || deliveryLocationAccountSelect.value.isDirty() || deliveryLocationChannelSelect.value.isDirty() || deliveryLocationGroupSelect.value.isDirty() || deliveryLocationCanalSelect.value.isDirty() || deliveryLocationPosTypeSelect.value.isDirty() || deliveryLocationPostalCodeSelect.value.isDirty() || deliveryLocationFreshnessNumber.value.isDirty() || deliveryLocationUnreachedKpiSelect.value.isDirty();
	} catch {
		return false;
	}
}

function resetFilters() {
	deliveryLocationTypeSelect.value.selectItems([]);
	deliveryLocationAccountSelect.value.selectItems([]);
	deliveryLocationChannelSelect.value.selectItems([]);
	deliveryLocationGroupSelect.value.selectItems([]);
	deliveryLocationCanalSelect.value.selectItems([]);
	deliveryLocationPosTypeSelect.value.selectItems([]);
	deliveryLocationPostalCodeSelect.value.selectItems([]);

	if (hasSFAProcess) {
		deliveryLocationFreshnessNumber.value.selectItems(null);
		deliveryLocationUnreachedKpiSelect.value.selectItems([]);
	}
}

defineExpose({
	originalLocationsUpdated,
	applyFilters,
});

// #endregion
</script>
